/* SystemBannerComponent.js */

"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");

const Renderer = require("./SystemBannerRenderer");
const GSO_ID = "565a65db-54e7-4461-a954-f0f38f310e19";

/**
 * @class SystemBannerComponent
 * @extends EventEmitter
 *
 * @fires SystemBannerComponent#Shutdown
 * @fires SystemBannerComponent#DataUpdated
 * @fires SystemBannerComponent#DirtyState
 * @fires SystemBannerComponent#OrganizationDataChange
 */

class SystemBannerComponent extends EventEmitter {
    constructor(
        OrganizationService,
        userSession,
        logger,
        userFeedback,
        SystemConfig,
        userService,
        steveIo,
        uiutils
    ) {
        super();
        this._OrganizationService = OrganizationService;
        this._userSession = userSession;
        this._logger = logger;
        this._modalUserFeedback = userFeedback;
        this._SystemConfig = SystemConfig;
        this._userService = userService;
        this._steveIo = steveIo;
        this._uiutils = uiutils;
        this._errors = undefined;

        this._systemConfigEntity = undefined;
        this._bannerData = undefined;
        // this._systemConfigFeed = undefined;

        this._organizationData = {};
        /**
         * @type {OsUpgradePlanPrefsEntity}
         * @private
         */
        this._osUpgradePlanPrefs = undefined;

        /**
         * @type {OsImageServerPrefs}
         * @private
         */
        this._osImageServerPrefs = undefined;

        this._orgId = _.get(userSession, "activeOrganization.id");
        this.defaultProperties = {
            "banner": {
                "enable": false,
                "text": "",
                "acceptButtonLabel": "Accept",
                "refusalButtonLabel": "Cancel",
                "displayMode": "ONCE", // ONCE for "Only on first sign-in per user" and ALWAYS for "On every sign-in".
                "updatedOn": 0
              }
        };
        this._updatedBannerData = {};
        this._isDirty = false;
        this._renderer = new Renderer(this);
    }


    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    loadOrganizationData() {
        return this._OrganizationService.getById({ id: this.activeOrgId }).then((orgData) => {
            this._organizationData = orgData;
            this._triggerOrganizationDataChanged(orgData);
            return orgData;
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    whenModelIsReady() {
        if (!_.isUndefined(this.activeOrgId)) {
            return Promise.all([
                this.getMyUser(),
                this.getSystemConfig()
            ]).then((values) => {
                this._systemConfigEntity = values[1];
                this._bannerData = _.get(this._systemConfigEntity, 'banner', undefined);
                if(!this._bannerData) {
                    this._bannerData = Object.assign({}, this.defaultProperties.banner);
                    // this._systemConfigEntity.setBanner(this._bannerData);
                    // this._systemConfigEntity.update();
                }
                // this._systemConfig = Object.values(this._systemConfigFeed._cache)[0];
                this._updatedBannerData = Object.assign({}, this._bannerData);
                return Promise.resolve();
            });
        } else {
            return Promise.reject();
        }
    }

    shutdown() {
        /**
         * @event SystemBannerComponent#Shutdown
         */
        this.emit("Shutdown");
        this._unregisterListeners();
        this.removeAllListeners();
        return Promise.all([
            this._renderer.shutdown()
        ]).then(
            () => {
                this._systemConfigEntity = undefined;
                this._SystemConfig = undefined;
                this._bannerData = undefined;
                this._updatedBannerData = undefined;
                this._isDirty = undefined;
                this._uiutils = undefined;
                this._errors = undefined;
                // this._systemConfigFeed = undefined;
            },
            (err) => {
                console.log("Error shutting down CustomFieldsComponent: ", err);
            }
        );
    }

    get logger() {
        return this._logger;
    }

    get userFeedback() {
        return this._modalUserFeedback;
    }

    get renderer() {
        return this.getRenderer();
    }

    getRenderer() {
        return this._renderer;
    }

    get activeOrgId() {
        return this.getActiveOrgId();
    }

    getOrganizationData() {
        return this._organizationData;
    }

    get activeOrgIsRoot() {
        return this._organizationData.providerId === null;
    }

    organizationChanged({ id, name }) {
        if (this._orgId === id) {
            return;
        }
        this._orgId = id;
        this._triggerOrganization({ id, name });
    }

    _triggerDataUpdated() {
        /**
         * @event SystemBannerComponent#DataUpdated
         */
        this.emit("DataUpdated");
    }

    _triggerDirtyStateChanged() {
        /**
         * @event SystemBannerComponent#DirtyState
         */
        const resultState =
            this._osImageServerPrefs.isDirty() || this._osUpgradePlanPrefs.isDirty();
        this.emit("DirtyState", resultState);
    }

    _triggerOrganization(org) {
        /**
         * @event SystemBannerComponent#Organization
         * @param {{id: string, name: string}} - new organization parameters.
         */
        this.emit("Organization", org);
    }

    _triggerOrganizationDataChanged(orgData) {
        /**
         * @event SystemBannerComponent#OrganizationDataChange
         * @param {Object} - new organization data.
         */
        this.emit("OrganizationDataChange", orgData);
    }

    getActiveOrgId() {
        return this._orgId;
    }

    get myUserInfo() {
        return this._myUserInfo;
    }

    getSystemConfig() {
        return this._SystemConfig.loadSystemConfig(GSO_ID);
        // return sysDetailsFeed.whenInitialized();
    }

    getMyUser() {
        let userRequest = {
            userId: this._userSession.getUser().getId(),
            descendantOrgActionGroupId: this._orgId
        };
        return this._userService.get(userRequest)
            .then((myInfo)=>{
                this._myUserInfo = myInfo;
                _.set(
                    this.defaultProperties,
                    "planPrefs.catalogCreationAuthGroup",
                    _.keys(myInfo.availableActionGroups)[0]
                );
            },
            (err) => {
                console.log("I am unable to request your user info.", err);
            }
        );
    }

    _saveBanner() {
        return Promise.resolve().then(() => {
            // if(!showVeil) {
            this._systemConfigEntity.setBanner(this._updatedBannerData);
            return this._systemConfigEntity.update().then(() => {
                this._bannerData = Object.assign({}, this._updatedBannerData);
                this._isDirty = false;
                return Promise.resolve(this._bannerData);
            })
            .catch((err) => {
                this._logger.warn(err);
                this._modalUserFeedback.displayError({errorCode: "Save Err", errorMsg: "Unable to save Banner configuration", err});
            });
            // } else {
            //     return this._modalUserFeedback.displayConfirmation("Save changes?")
            //     .then(() => {
            //         // console.log('clicked ok');
            //         this._bannerData = Object.assign({}, this._updatedBannerData);
            //         this._systemConfigEntity.setBanner(this._updatedBannerData);
            //         return this._systemConfigEntity.update().then(() => {
            //             this._isDirty = false;
            //             return Promise.resolve(this._bannerData);
            //         });
            //     })
            //     .catch((err) => {
            //         this._logger.warn(err);
            //         this._modalUserFeedback.displayError({errorCode: "Save Err", errorMsg: "Unable to save Banner configuration", err});
            //     });
            // }
        });
    }

    _cancelBannerChanges() {
        // return this._modalUserFeedback.displayConfirmation("Are you sure you want to cancel?",{
        //     title: "Cancel Changes",
        //         confirmDetails: {
        //             text: "Confirm",
        //             type: "destructive"
        //         },
        //         rejectDetails: {
        //             text: "Cancel",
        //             type: "text"
        //         }
        //     }
        // )
        return Promise.resolve()
        .then(() => {
            this._updatedBannerData = Object.assign({}, this._bannerData);
            this._isDirty = false;
            return Promise.resolve(this._bannerData);
        })
        .catch(err => {
            this._logger.warn(err);
        });
    }

    _displayError(err) {
        const errorMsg = err.reduce((msg, err) => {
            return `${msg}\n${err.key}: ${err.err}`;
        },"");
        this._uiutils.writeErrorMessage(`Error: ${errorMsg}`);
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/


    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = SystemBannerComponent;

SystemBannerComponent.$groupName = "Global";

SystemBannerComponent.$displayName = "Banner";
SystemBannerComponent.$license = ["GLUWARE"];

SystemBannerComponent.$inject = [
    "$teve.services.instance.Organization",
    "$teve.userSession",
    "$teve.logger",
    "$teve.ui.userFeedback",
    "$teve.entities.SystemConfig",
    "$teve.services.instance.User",
    "$teve.io",
    "$teve.ui.UiUtils"
];
